import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, openSecureFile } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PartiInline } from "../components/CrediusContabilita";
import {
  FileText, Search, Loader2, Download, Wallet, Receipt, Euro,
  ArrowDownCircle, ArrowUpCircle, Scale, ChevronDown, ChevronRight,
} from "lucide-react";

const fmtEUR = (v) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

const STATO_STYLE = {
  "Autorizzata": "bg-amber-100 text-amber-800 border-amber-300",
  "Pagata": "bg-emerald-700 text-white border-emerald-800",
  "Fatturata": "bg-indigo-100 text-indigo-800 border-indigo-300",
  "Emessa": "bg-amber-100 text-amber-800 border-amber-300",
};

// Iter122k122/k124 · Contabilità Avvocato con due sotto-sezioni:
//   - AVERE: note pro-forma e fatture verso i clienti (competenze legali → incassi)
//   - DARE : ricorrenze fee che lo Studio richiede all'avvocato (uscite)
export default function CrediusContabilitaAvvocato() {
  const [tab, setTab] = useState("avere"); // "avere" | "dare"

  // Iter122k123 · marca la sezione "Contabilità" come vista → azzera il badge rosso
  useEffect(() => {
    api.post("/me/last-seen/contabilita_avv").catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="credius-contabilita-avvocato">
      <div className="flex items-center gap-2">
        <Wallet className="w-6 h-6 text-indigo-900" />
        <h1 className="text-2xl font-bold text-indigo-900">Contabilità</h1>
      </div>

      {/* Tab menu orizzontale */}
      <div className="flex gap-1 border-b-2 border-slate-200" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "avere"}
          data-testid="tab-avere"
          onClick={() => setTab("avere")}
          className={`flex items-center gap-2 px-4 py-2 -mb-[2px] border-b-2 font-bold text-sm transition-colors ${
            tab === "avere"
              ? "border-emerald-600 text-emerald-800"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <ArrowDownCircle className="w-4 h-4" /> Avere
          <span className="text-[10px] font-normal text-slate-400 hidden sm:inline">
            · verso i clienti
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "dare"}
          data-testid="tab-dare"
          onClick={() => setTab("dare")}
          className={`flex items-center gap-2 px-4 py-2 -mb-[2px] border-b-2 font-bold text-sm transition-colors ${
            tab === "dare"
              ? "border-red-600 text-red-800"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <ArrowUpCircle className="w-4 h-4" /> Dare
          <span className="text-[10px] font-normal text-slate-400 hidden sm:inline">
            · verso lo Studio
          </span>
        </button>
      </div>

      {tab === "avere" ? <TabAvere /> : <TabDare />}
    </div>
  );
}

// ============================================================================
// TAB "AVERE" — note pro-forma / fatture emesse verso i clienti della pratica
// ============================================================================
function TabAvere() {
  const navigate = useNavigate();
  const [notule, setNotule] = useState(null);
  const [filtri, setFiltri] = useState({
    q: "", stato: "Pagata", from_date: "", to_date: "",
  });
  const [filtriOpen, setFiltriOpen] = useState(false);
  const filtriAttivi = Object.values(filtri).filter((v) => v && String(v).trim() !== "").length;

  const load = () => {
    const params = new URLSearchParams();
    Object.entries(filtri).forEach(([k, v]) => { if (v) params.set(k, v); });
    api.get(`/credius/avvocato/notule?${params}`)
      .then((r) => setNotule(r.data))
      .catch(() => setNotule([]));
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [filtri]); // eslint-disable-line

  const totali = (notule || []).reduce((acc, n) => {
    acc.count += 1;
    if (n.stato === "Pagata") acc.pagate += (n.totale_netto || 0);
    if (n.stato === "Autorizzata") acc.autorizzate += (n.totale_netto || 0);
    if (n.stato === "Fatturata") acc.fatturate += (n.totale_netto || 0);
    return acc;
  }, { count: 0, pagate: 0, autorizzate: 0, fatturate: 0 });

  return (
    <div className="space-y-4" data-testid="pane-avere">
      <p className="text-sm text-slate-600">
        Note pro-forma e fatture elettroniche <b>emesse verso i clienti</b> a fronte
        delle competenze legali sulle pratiche affidate.
      </p>

      {/* Widgets */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Totale note" value={totali.count} isCount />
        <StatCard label="In attesa pagamento" value={totali.autorizzate} color="amber" />
        <StatCard label="Pagate" value={totali.pagate} color="emerald" />
        <StatCard label="Fatturate" value={totali.fatturate} color="indigo" />
      </div>

      {/* Filtri richiudibili (Iter122k131) */}
      <FiltriCollapsible open={filtriOpen} setOpen={setFiltriOpen} count={filtriAttivi}
        onReset={() => setFiltri({ q: "", stato: "Pagata", from_date: "", to_date: "" })}
        testidPrefix="avv-notule">
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Testo</Label>
            <Input data-testid="avv-notule-q" value={filtri.q}
              onChange={(e) => setFiltri({ ...filtri, q: e.target.value })}
              placeholder="Codice, pratica, cliente..." className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Stato</Label>
            <select data-testid="avv-notule-stato" value={filtri.stato}
              onChange={(e) => setFiltri({ ...filtri, stato: e.target.value })}
              className="w-full h-9 mt-1 border border-slate-300 rounded-md px-2 text-sm">
              <option value="">Tutti</option>
              <option value="Autorizzata">Autorizzata (in attesa pagamento)</option>
              <option value="Pagata">Pagata</option>
              <option value="Fatturata">Fatturata</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Da</Label>
            <Input type="date" data-testid="avv-notule-from" value={filtri.from_date}
              onChange={(e) => setFiltri({ ...filtri, from_date: e.target.value })}
              className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">A</Label>
            <Input type="date" data-testid="avv-notule-to" value={filtri.to_date}
              onChange={(e) => setFiltri({ ...filtri, to_date: e.target.value })}
              className="h-9 mt-1" />
          </div>
        </div>
      </FiltriCollapsible>

      {/* Elenco */}
      {notule === null ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-7 h-7 animate-spin text-indigo-800" />
        </div>
      ) : notule.length === 0 ? (
        <div data-testid="avv-notule-empty" className="text-center py-12 text-slate-500">
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          Nessuna nota trovata per i filtri selezionati.
        </div>
      ) : (
        <div className="space-y-2">
          {notule.map((n) => (
            <div key={n.id} data-testid={`avv-notula-row-${n.id}`}
              className="bg-white rounded-xl border-2 border-slate-200 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATO_STYLE[n.stato] || "bg-slate-100 border-slate-300 text-slate-700"}`}>
                  {n.stato}
                </span>
                <span className="font-mono text-xs text-slate-600">{n.codice_documento}</span>
                <button type="button" onClick={() => navigate(`/credius/${n.pratica_id}`)}
                  className="inline-flex items-center gap-1 font-bold text-indigo-800 hover:underline"
                  data-testid={`avv-notula-pratica-${n.id}`}>
                  <FileText className="w-4 h-4" /> {n.pratica_numero}
                </button>
                <PartiInline clienti={n.pratica_clienti} controparti={n.pratica_controparti} />
                <span className="text-xs text-slate-500">
                  {n.pratica_tipo} · {n.pratica_interna ? "Interna" : "Esterna"}
                </span>
                <span className="text-xs text-slate-500 ml-auto">Emessa il {fmtD(n.created_at)}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-600">
                  Cliente: <b>{(n.clienti || []).map((c) => c.nome).join(", ") || "—"}</b>
                </span>
                <span className="text-slate-500">· Imponibile: {fmtEUR(n.imponibile)}</span>
                <span className="text-slate-500">· IVA: {fmtEUR(n.iva)}</span>
                <span className="ml-auto font-bold text-slate-900">Netto: {fmtEUR(n.totale_netto)}</span>
              </div>

              {(n.stato === "Pagata" || n.stato === "Fatturata") && n.pagata_at && (
                <div className="text-xs text-emerald-700 italic flex items-center gap-1">
                  <Euro className="w-3 h-3" /> Pagata il {fmtD(n.pagata_at)}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline"
                  data-testid={`avv-notula-pdf-${n.id}`}
                  onClick={() => openSecureFile(`/credius/notule/${n.id}/pdf`)}
                  className="border-indigo-300 text-indigo-800 hover:bg-indigo-50">
                  <Download className="w-4 h-4 mr-1" /> PDF Notula
                </Button>
                {n.fattura_file_id && (
                  <Button size="sm" variant="outline"
                    data-testid={`avv-notula-fattura-${n.id}`}
                    onClick={() => openSecureFile(`/files/${n.fattura_file_id}`)}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                    <Receipt className="w-4 h-4 mr-1" />
                    Fattura {n.fattura_numero ? `n. ${n.fattura_numero}` : "elettronica"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TAB "DARE" — ricorrenze fee che lo Studio richiede all'avvocato convenzionato
// ============================================================================
function TabDare() {
  const [ricorrenze, setRicorrenze] = useState(null);
  const [filtri, setFiltri] = useState({
    q: "", stato: "", from_date: "", to_date: "",
  });
  const [filtriOpen, setFiltriOpen] = useState(false);
  const filtriAttivi = Object.values(filtri).filter((v) => v && String(v).trim() !== "").length;

  const load = () => {
    const params = new URLSearchParams();
    Object.entries(filtri).forEach(([k, v]) => { if (v) params.set(k, v); });
    api.get(`/credius/avvocato/ricorrenze?${params}`)
      .then((r) => setRicorrenze(r.data))
      .catch(() => setRicorrenze([]));
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [filtri]); // eslint-disable-line

  const totali = (ricorrenze || []).reduce((acc, r) => {
    acc.count += 1;
    if (r.stato === "Emessa") acc.da_pagare += (r.imponibile_totale || 0);
    if (r.stato === "Pagata") acc.pagate += (r.imponibile_totale || 0);
    return acc;
  }, { count: 0, da_pagare: 0, pagate: 0 });

  return (
    <div className="space-y-4" data-testid="pane-dare">
      <p className="text-sm text-slate-600">
        Note pro-forma che <b>lo Studio</b> emette nei tuoi confronti per richiedere il
        pagamento delle <b>fee</b> sulle pratiche affidate (percentuale sull'incasso
        delle competenze).
      </p>

      {/* Widgets */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Totale ricorrenze" value={totali.count} isCount />
        <StatCard label="Da pagare (Emesse)" value={totali.da_pagare} color="amber" />
        <StatCard label="Pagate" value={totali.pagate} color="emerald" />
      </div>

      {/* Filtri richiudibili (Iter122k131) */}
      <FiltriCollapsible open={filtriOpen} setOpen={setFiltriOpen} count={filtriAttivi}
        onReset={() => setFiltri({ q: "", stato: "", from_date: "", to_date: "" })}
        testidPrefix="avv-ric">
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Testo</Label>
            <Input data-testid="avv-ric-q" value={filtri.q}
              onChange={(e) => setFiltri({ ...filtri, q: e.target.value })}
              placeholder="Codice, pratica..." className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Stato</Label>
            <select data-testid="avv-ric-stato" value={filtri.stato}
              onChange={(e) => setFiltri({ ...filtri, stato: e.target.value })}
              className="w-full h-9 mt-1 border border-slate-300 rounded-md px-2 text-sm">
              <option value="">Tutti</option>
              <option value="Emessa">Emessa (da pagare)</option>
              <option value="Pagata">Pagata</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Da</Label>
            <Input type="date" data-testid="avv-ric-from" value={filtri.from_date}
              onChange={(e) => setFiltri({ ...filtri, from_date: e.target.value })}
              className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">A</Label>
            <Input type="date" data-testid="avv-ric-to" value={filtri.to_date}
              onChange={(e) => setFiltri({ ...filtri, to_date: e.target.value })}
              className="h-9 mt-1" />
          </div>
        </div>
      </FiltriCollapsible>

      {/* Elenco */}
      {ricorrenze === null ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-7 h-7 animate-spin text-red-800" />
        </div>
      ) : ricorrenze.length === 0 ? (
        <div data-testid="avv-ric-empty" className="text-center py-12 text-slate-500">
          <Scale className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          Nessuna ricorrenza fee ricevuta dallo Studio.
        </div>
      ) : (
        <div className="space-y-2">
          {ricorrenze.map((r) => (
            <div key={r.id} data-testid={`avv-ric-row-${r.id}`}
              className="bg-white rounded-xl border-2 border-slate-200 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATO_STYLE[r.stato] || "bg-slate-100 border-slate-300 text-slate-700"}`}>
                  {r.stato}
                </span>
                <span className="font-mono text-xs text-slate-600">{r.codice_documento}</span>
                <span className="text-xs text-slate-500 ml-auto">
                  Emessa il {fmtD(r.created_at)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-600">
                  Pratiche incluse: <b>{(r.pratiche || []).length}</b>
                </span>
                <span className="ml-auto font-bold text-slate-900">
                  Fee totale: {fmtEUR(r.imponibile_totale)}
                </span>
              </div>
              {r.pagata_at && (
                <div className="text-xs text-emerald-700 italic flex items-center gap-1">
                  <Euro className="w-3 h-3" /> Pagata il {fmtD(r.pagata_at)}
                </div>
              )}
              {r.note && (
                <div className="text-xs text-slate-500 italic">Nota Studio: {r.note}</div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline"
                  data-testid={`avv-ric-pdf-${r.id}`}
                  onClick={() => openSecureFile(`/credius/ricorrenze/${r.id}/pdf`)}
                  className="border-red-300 text-red-800 hover:bg-red-50">
                  <Download className="w-4 h-4 mr-1" /> PDF Fee
                </Button>
                {/* Iter123k175 · Nota Pro-forma accompagnatoria */}
                <Button size="sm" variant="outline"
                  data-testid={`avv-ric-proforma-${r.id}`}
                  onClick={() => openSecureFile(`/credius/ricorrenze/${r.id}/proforma-pdf`)}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50">
                  <Receipt className="w-4 h-4 mr-1" /> Nota P.F.
                </Button>
                {r.fattura_file_id && (
                  <Button size="sm" variant="outline"
                    data-testid={`avv-ric-fattura-${r.id}`}
                    onClick={() => openSecureFile(`/files/${r.fattura_file_id}`)}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                    <Receipt className="w-4 h-4 mr-1" /> Fattura Studio
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Iter122k131 · Contenitore filtri richiudibile riusabile
function FiltriCollapsible({ open, setOpen, count = 0, onReset, testidPrefix = "filtri", children }) {
  return (
    <div className="bg-white rounded-xl border-2 border-slate-200 p-3 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" data-testid={`${testidPrefix}-filtri-toggle`}
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 text-xs font-bold uppercase text-slate-600 hover:text-slate-900 transition-colors"
          aria-expanded={open}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Search className="w-4 h-4" /> Ricerca & Filtri
          {count > 0 && (
            <span data-testid={`${testidPrefix}-filtri-count`}
              className="ml-1 text-[10px] font-bold bg-indigo-600 text-white rounded-full px-1.5 py-0.5">
              {count}
            </span>
          )}
        </button>
        {count > 0 && (
          <button type="button" data-testid={`${testidPrefix}-filtri-reset`}
            onClick={onReset}
            className="text-[10px] font-semibold text-red-700 hover:text-red-900 underline">
            Azzera
          </button>
        )}
      </div>
      {open && children}
    </div>
  );
}


function StatCard({ label, value, color = "indigo", isCount = false }) {
  const bg = {
    amber: "bg-amber-50 border-amber-300",
    emerald: "bg-emerald-50 border-emerald-300",
    slate: "bg-slate-50 border-slate-300",
    indigo: "bg-indigo-50 border-indigo-300",
  }[color] || "bg-white";
  const text = {
    amber: "text-amber-800",
    emerald: "text-emerald-800",
    slate: "text-slate-700",
    indigo: "text-indigo-900",
  }[color] || "text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-xs font-bold uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-bold ${text}`}>{isCount ? value : fmtEUR(value)}</div>
    </div>
  );
}
